import {
  buildSchema,
  GraphQLInputObjectType,
  GraphQLList,
  GraphQLNonNull,
  GraphQLScalarType,
  GraphQLEnumType,
} from "graphql";
import * as XLSX from "xlsx";
import { getOperationDefinition } from "./operationUtils";

interface BFSNode {
  path: string;
  type: any;
  isList: boolean;
  isScalarList?: boolean;
  scalarTypeName?: string;
}

export function generateTemplate(
  schemaSDL: string,
  operationName: string,
  operationType: "mutation" | "query" = "mutation",
): XLSX.WorkBook {
  const mutation = getOperationDefinition(
    schemaSDL,
    operationType,
    operationName,
  );

  const workbook = XLSX.utils.book_new();

  const queue: BFSNode[] = [];

  // Always generate a single "variables" sheet for root scalars
  const wsData: any[][] = [];
  wsData.push(["variables"]);
  const headers: string[] = ["rowRef"];
  const sampleRow: any[] = ["ROW1"];

  mutation.args.forEach((arg) => {
    let t: any = arg.type;
    if (t instanceof GraphQLNonNull) t = t.ofType;
    if (t instanceof GraphQLList) {
      const item =
        t.ofType instanceof GraphQLNonNull ? t.ofType.ofType : t.ofType;
      if (isInputObject(item)) {
        queue.push({ path: arg.name, type: item, isList: true });
      } else {
        // Scalar list - keep column in variables sheet (backward compatibility)
        headers.push(arg.name);
        sampleRow.push(sampleForType(t));
        // Also generate separate sheet for large data
        queue.push({
          path: arg.name,
          type: item,
          isList: true,
          isScalarList: true,
          scalarTypeName: item.name,
        });
      }
      return;
    }
    if (isInputObject(t)) {
      const fields = (t as GraphQLInputObjectType).getFields();
      for (const fieldName of Object.keys(fields)) {
        let ft: any = fields[fieldName].type;
        let isListField = false;
        if (ft instanceof GraphQLNonNull) ft = ft.ofType;
        if (ft instanceof GraphQLList) {
          isListField = true;
          const inner =
            ft.ofType instanceof GraphQLNonNull ? ft.ofType.ofType : ft.ofType;
          if (isInputObject(inner)) {
            headers.push(`${arg.name}.${fieldName}`);
            sampleRow.push(undefined);
            queue.push({
              path: `${arg.name}.${fieldName}`,
              type: inner,
              isList: true,
            });
            continue;
          } else {
            // Scalar list inside input object - keep column (backward compatibility)
            headers.push(`${arg.name}.${fieldName}`);
            sampleRow.push(sampleForType(ft));
            // Also generate separate sheet for large data
            queue.push({
              path: `${arg.name}.${fieldName}`,
              type: inner,
              isList: true,
              isScalarList: true,
              scalarTypeName: inner.name,
            });
            continue;
          }
        }
        if (!isInputObject(ft)) {
          headers.push(`${arg.name}.${fieldName}`);
          sampleRow.push(sampleForType(fields[fieldName].type));
        }
        if (isInputObject(ft)) {
          queue.push({
            path: `${arg.name}.${fieldName}`,
            type: ft,
            isList: isListField,
          });
        }
      }
    } else {
      headers.push(arg.name);
      sampleRow.push(sampleForType(t));
    }
  });

  wsData.push(headers);
  wsData.push(sampleRow);

  const sheet = XLSX.utils.aoa_to_sheet(wsData);
  XLSX.utils.book_append_sheet(workbook, sheet, "variables");

  while (queue.length) {
    const node = queue.shift()!;
    const sheetName = sanitizeSheetName(node.path);
    const wsData: any[][] = [];

    // row 0 – sheet path
    wsData.push([node.path]);

    // Handle scalar list sheets differently
    if (node.isScalarList) {
      // Scalar list sheet: _parentRef + value (or rowRef + value for root level)
      const headers = node.path.includes(".")
        ? ["_parentRef", "value"]
        : ["rowRef", "value"];
      wsData.push(headers);

      const sampleValue = sampleForScalarType(node.scalarTypeName);
      const sampleRow = node.path.includes(".")
        ? ["PARENT1", sampleValue]
        : ["ROW1", sampleValue];
      wsData.push(sampleRow);

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      continue;
    }

    // row 1 – headers
    const headers: string[] = [];
    if (node.path.includes(".")) {
      headers.push("_parentRef", "childRowRef");
    } else {
      headers.push("rowRef");
    }

    // collect child scalar fields & enqueue nested objects
    if (isInputObject(node.type)) {
      const fields = (node.type as GraphQLInputObjectType).getFields();
      for (const fieldName of Object.keys(fields)) {
        let ft: any = fields[fieldName].type;
        let isListField = false;
        if (ft instanceof GraphQLNonNull) ft = ft.ofType;
        if (ft instanceof GraphQLList) {
          isListField = true;
          const inner =
            ft.ofType instanceof GraphQLNonNull ? ft.ofType.ofType : ft.ofType;
          if (isInputObject(inner)) {
            headers.push(fieldName);
            queue.push({
              path: `${node.path}.${fieldName}`,
              type: inner,
              isList: true,
            });
            continue;
          } else {
            // Scalar list in nested object - keep column (backward compatibility)
            headers.push(fieldName);
            // Also generate separate sheet for large data
            queue.push({
              path: `${node.path}.${fieldName}`,
              type: inner,
              isList: true,
              isScalarList: true,
              scalarTypeName: inner.name,
            });
            continue;
          }
        }
        if (!isInputObject(ft)) {
          headers.push(fieldName);
        }
        if (isInputObject(ft)) {
          queue.push({
            path: `${node.path}.${fieldName}`,
            type: ft,
            isList: isListField,
          });
        }
      }
    }
    wsData.push(headers);

    // row 2 – sample row (ignored during parsing)
    const sampleRow: any[] = headers.map((h, idx) => {
      if (idx === 0) return node.path.includes(".") ? "PARENT1" : "ROW1";
      if (node.path.includes(".") && idx === 1) return "CHILD1";
      const fieldName = h;
      if (!isInputObject(node.type)) return "";
      const field = (node.type as GraphQLInputObjectType).getFields()[
        fieldName
      ];
      if (!field) return "";
      return sampleForType(field.type);
    });
    wsData.push(sampleRow);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(workbook, ws, sheetName);
  }

  return workbook;
}

function isInputObject(type: any): type is GraphQLInputObjectType {
  return type instanceof GraphQLInputObjectType;
}

function sampleForScalarType(typeName?: string): any {
  switch (typeName) {
    case "Int":
      return 42;
    case "Float":
      return 3.14;
    case "Boolean":
      return true;
    case "ID":
      return "ID1";
    case "JSON":
      return "JSON";
    default:
      return "TEXT";
  }
}

function sampleForType(type: any): any {
  let t = type;
  if (t instanceof GraphQLNonNull) t = t.ofType;
  if (t instanceof GraphQLList) {
    const inner =
      t.ofType instanceof GraphQLNonNull ? t.ofType.ofType : t.ofType;
    if (!isInputObject(inner)) {
      const s = sampleForType(inner);
      return `${s},${s}`;
    }
    t = inner;
  }
  if (t instanceof GraphQLScalarType) {
    switch (t.name) {
      case "Int":
        return 42;
      case "Float":
        return 3.14;
      case "Boolean":
        return true;
      case "ID":
        return "ID1";
      case "JSON":
        return "JSON";
      default:
        return "TEXT";
    }
  }
  if (t instanceof GraphQLEnumType) {
    return t.getValues()[0]?.value ?? "VAL1";
  }
  return "";
}

function sanitizeSheetName(path: string): string {
  const disallowed = /[\\\/\?\*\[\]\:]/g;
  let name = path.replace(disallowed, "_");
  if (name.length > 31) {
    const hash = name
      .split("")
      .reduce((acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) >>> 0, 0)
      .toString(16);
    const baseLen = 31 - hash.length - 1;
    name = name.slice(0, Math.max(8, baseLen)) + "_" + hash;
    if (name.length > 31) name = name.slice(0, 31);
  }
  return name;
}
